import React, { useState, useEffect } from 'react';

export default function Settings() {
  const [wiseStatus, setWiseStatus] = useState(null);
  const [apiToken, setApiToken] = useState('');
  const [profileId, setProfileId] = useState('');
  const [sandbox, setSandbox] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [balances, setBalances] = useState(null);

  useEffect(() => {
    fetch('/api/wise/status').then(r => r.json()).then(setWiseStatus).catch(() => {});
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setTestResult(null);
    try {
      const resp = await fetch('/api/wise/configure', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ apiToken: apiToken || undefined, profileId: profileId || undefined, sandbox }),
      });
      const data = await resp.json();
      setWiseStatus(data.config);
      setTestResult({ success: true, message: 'Configuration saved' });
    } catch {
      setTestResult({ success: false, message: 'Failed to save' });
    }
    setSaving(false);
  };

  const handleTest = async () => {
    setTesting(true);
    setTestResult(null);
    setBalances(null);
    try {
      const resp = await fetch('/api/wise/test');
      const data = await resp.json();
      if (data.success) {
        setBalances(data.balances);
        setTestResult({ success: true, message: 'Connected successfully!' });
      } else {
        setTestResult({ success: false, message: data.error?.message || JSON.stringify(data.error) || 'Connection failed' });
      }
    } catch (err) {
      setTestResult({ success: false, message: 'Network error' });
    }
    setTesting(false);
  };

  return (
    <div className="gap-16">
      {/* Header */}
      <div className="card">
        <div className="card-body" style={{ padding: '32px 40px' }}>
          <div style={{ fontFamily: "'Source Serif 4', serif", fontSize: 22, color: 'var(--navy)', fontWeight: 700 }}>
            Payment Gateway Settings
          </div>
          <p style={{ color: 'var(--text2)', margin: '8px 0 0', fontSize: 13 }}>
            Configure real payment providers to send actual international transfers.
          </p>
        </div>
      </div>

      {/* Wise Integration */}
      <div className="card">
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'linear-gradient(135deg, #9FE870, #00B9FF)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: '#fff', fontSize: 18, fontWeight: 700,
            }}>W</div>
            <div>
              <span className="card-title">Wise Business API</span>
              <div style={{ fontSize: 11, color: 'var(--text3)' }}>Real international transfers via SWIFT & local rails</div>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{
              fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 6,
              background: wiseStatus?.configured ? 'rgba(13,148,136,0.08)' : 'rgba(231,76,111,0.08)',
              color: wiseStatus?.configured ? 'var(--teal)' : 'var(--coral)',
              border: `1px solid ${wiseStatus?.configured ? 'rgba(13,148,136,0.2)' : 'rgba(231,76,111,0.2)'}`,
            }}>
              {wiseStatus?.configured ? 'CONNECTED' : 'NOT CONFIGURED'}
            </span>
            {wiseStatus?.sandbox && wiseStatus?.configured && (
              <span style={{
                fontSize: 10, fontWeight: 700, padding: '4px 12px', borderRadius: 6,
                background: 'rgba(245,158,11,0.08)', color: '#F59E0B',
                border: '1px solid rgba(245,158,11,0.2)',
              }}>SANDBOX</span>
            )}
          </div>
        </div>
        <div className="card-body">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            {/* How it works */}
            <div style={{
              padding: '16px 20px', borderRadius: 'var(--radius)',
              background: 'var(--surface2)', border: '1px solid var(--border-light)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--gold)', marginBottom: 8 }}>HOW IT WORKS</div>
              <div style={{ display: 'flex', gap: 24 }}>
                {[
                  ['1', 'Create a Wise Business account at wise.com'],
                  ['2', 'Generate an API token in Settings > API tokens'],
                  ['3', 'Copy your Profile ID from the URL or API'],
                  ['4', 'Paste credentials below and test connection'],
                  ['5', 'All Send via API transfers will use Wise'],
                ].map(([n, text]) => (
                  <div key={n} style={{ flex: 1, display: 'flex', gap: 8 }}>
                    <span style={{
                      width: 22, height: 22, borderRadius: '50%', flexShrink: 0,
                      background: 'var(--gold)', color: '#fff', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700,
                    }}>{n}</span>
                    <span style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.4 }}>{text}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Credentials */}
            <div className="grid2" style={{ gap: 16 }}>
              <div className="form-group">
                <label className="form-label">API Token *</label>
                <input className="form-input" type="password" value={apiToken}
                  onChange={e => setApiToken(e.target.value)}
                  placeholder={wiseStatus?.hasToken ? '(token saved — enter new to change)' : 'Enter your Wise API token'} />
              </div>
              <div className="form-group">
                <label className="form-label">Profile ID *</label>
                <input className="form-input" value={profileId}
                  onChange={e => setProfileId(e.target.value)}
                  placeholder={wiseStatus?.profileId ? `Current: ${wiseStatus.profileId}` : 'Enter your Wise Profile ID'} />
              </div>
            </div>

            {/* Mode */}
            <div>
              <div className="form-label" style={{ marginBottom: 8 }}>Environment</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[
                  { val: true, label: 'Sandbox', desc: 'Test mode — no real money sent' },
                  { val: false, label: 'Production', desc: 'Live mode — real money transfers' },
                ].map(opt => (
                  <div key={String(opt.val)} onClick={() => setSandbox(opt.val)} style={{
                    flex: 1, padding: '14px 18px', borderRadius: 'var(--radius)', cursor: 'pointer',
                    border: sandbox === opt.val ? '2px solid var(--gold)' : '1px solid var(--border)',
                    background: sandbox === opt.val ? 'var(--gold-soft)' : 'var(--surface)',
                    transition: 'all 0.15s',
                  }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--navy)' }}>{opt.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-gold" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : 'Save Configuration'}
              </button>
              <button className="btn btn-teal" onClick={handleTest} disabled={testing || !wiseStatus?.configured}>
                {testing ? 'Testing...' : 'Test Connection'}
              </button>
            </div>

            {/* Test Result */}
            {testResult && (
              <div className={`alert ${testResult.success ? 'alert-success' : 'alert-error'}`}>
                {testResult.success ? '\u2713' : '\u2717'} {testResult.message}
              </div>
            )}

            {/* Balances */}
            {balances && balances.length > 0 && (
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.15em', color: 'var(--teal)', marginBottom: 8 }}>
                  WISE ACCOUNT BALANCES
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {balances.map((b, i) => (
                    <div key={i} style={{
                      padding: '12px 18px', borderRadius: 'var(--radius)',
                      background: 'var(--teal-glow)', border: '1px solid rgba(13,148,136,0.2)',
                      minWidth: 140,
                    }}>
                      <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 600 }}>{b.currency}</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--teal)', fontFamily: "'IBM Plex Mono', monospace" }}>
                        {b.amount?.value?.toLocaleString('en-US', { minimumFractionDigits: 2 }) || '0.00'}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Supported Currencies */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">Supported Transfer Methods</span>
        </div>
        <div className="card-body">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {[
              { currency: 'USD', method: 'Fedwire / ACH', country: 'United States' },
              { currency: 'THB', method: 'PromptPay / BAHTNET', country: 'Thailand' },
              { currency: 'EUR', method: 'SEPA / SWIFT', country: 'Europe' },
              { currency: 'GBP', method: 'Faster Payments / CHAPS', country: 'United Kingdom' },
              { currency: 'JPY', method: 'Zengin / SWIFT', country: 'Japan' },
              { currency: 'SGD', method: 'FAST / SWIFT', country: 'Singapore' },
              { currency: 'HKD', method: 'FPS / SWIFT', country: 'Hong Kong' },
              { currency: 'AUD', method: 'NPP / SWIFT', country: 'Australia' },
              { currency: 'CHF', method: 'SIC / SWIFT', country: 'Switzerland' },
              { currency: 'CNY', method: 'CNAPS / SWIFT', country: 'China' },
              { currency: 'INR', method: 'IMPS / NEFT', country: 'India' },
              { currency: 'MYR', method: 'DuitNow / SWIFT', country: 'Malaysia' },
            ].map(c => (
              <div key={c.currency} style={{
                padding: '12px 16px', borderRadius: 'var(--radius)',
                border: '1px solid var(--border-light)', background: 'var(--surface)',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--navy)' }}>{c.currency}</span>
                  <span style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 600 }}>{c.method}</span>
                </div>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{c.country}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
